import React, { useState } from 'react';
import { Box, Typography, Button, createTheme, IconButton } from '@mui/material';
import { ThemeProvider } from 'react-bootstrap';
import FolderCheck from '../../../assets/svg/folder_check.svg?react';
import CloudUploadIcon from '../../../assets/svg/feather_upload-cloud.svg?react';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

interface FileUploadProps {
  onFileSelect: (files: File[]) => void;
  accept?: string;
  supportedFormatsKey?: string;
}

const FileUploadForm: React.FC<FileUploadProps> = ({
  onFileSelect,
  accept,
  supportedFormatsKey,
}) => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const maxFiles = 5;

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);

    // Limit to max files
    const newFiles = [...selectedFiles];
    droppedFiles.forEach((file) => {
      if (newFiles.length < maxFiles) {
        newFiles.push(file);
      }
    });

    setSelectedFiles(newFiles);
    onFileSelect(newFiles);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const fileList = Array.from(event.target.files);

      // Limit to max files
      const newFiles = [...selectedFiles];
      fileList.forEach((file) => {
        if (newFiles.length < maxFiles) {
          newFiles.push(file);
        }
      });

      setSelectedFiles(newFiles);
      onFileSelect(newFiles);
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    onFileSelect(updatedFiles);
  };

  const localTheme = createTheme({});

  return (
    <ThemeProvider theme={localTheme}>
      <Box
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className='file-upload-container'
      >
        <Typography variant='h2' className='select-text' color='textSecondary'>
          {t('messages.selectFilesMessage')}
        </Typography>
        {selectedFiles.length > 0 && (
          <Box className='selected-files-container'>
            {selectedFiles.map((file, index) => (
              <Box key={`${file.name}-${index}`} className='selected-file-text'>
                <FolderCheck />
                <Typography variant='caption'>{file.name}</Typography>
                <IconButton
                  size='small'
                  onClick={() => handleRemoveFile(index)}
                  className='remove-file-button'
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Box>
            ))}
            {selectedFiles.length < maxFiles && (
              <Button
                component='label'
                htmlFor='file-input'
                variant='text'
                color='primary'
                className='add-more-files-button'
              >
                {t('buttons.addMoreFiles')} ({maxFiles - selectedFiles.length}{' '}
                {t('labels.remaining')})
              </Button>
            )}
          </Box>
        )}
        <input
          type='file'
          style={{ display: 'none' }}
          id='file-input'
          accept={accept ?? 'image/*, .pdf, .doc, .docx'}
          onChange={handleFileSelect}
          multiple
        />
        {selectedFiles.length === 0 && (
          <Box className='button-container'>
            <CloudUploadIcon />
            <Button
              component='label'
              htmlFor='file-input'
              variant='text'
              color='primary'
              className='select-file-button'
            >
              {t('buttons.selectFiles')}
            </Button>
            <div style={{ fontSize: '0.7rem', color: 'gray' }}>
              {t(supportedFormatsKey ?? 'messages.supportedFormats')}
            </div>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default FileUploadForm;
